import { rem } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  AVAILABILITY_SLOTS,
  AVAILABILITY_VALUE_LABEL,
  EXPERIENCE_LEVELS,
  GENDERS,
  isCancelled,
  joinNames,
  mailboxSiblings,
  type AvailabilityValue,
  noteNeedsViewer,
} from '@reditelji/shared';
import { useEffect, useMemo, useState } from 'react';
import { canonical, sameTrimmed, useDraft, type FieldSame } from '../lib/draft';
import { useNavigate } from 'react-router';
import { useEvent, useShiftAnswerMutation } from '../api/hooks';
import { useSession } from '../api/session';
import type { EventDay, ShiftAnswerView } from '../api/types';
import {
  useCancelPlan,
  useThread,
  useVolunteerMutations,
  useVolunteers,
  type CancelFreedShift,
  type VolunteerRow,
} from '../api/volunteers';
import type { TagRow } from '../api/tags';
import { useDeepLinks } from '../lib/deepLinks';
import { CancelDialog } from './CancelDialog';
import { CancelledCard, ComeBackButton } from './CancelledCard';
import { comeBackDone, freedPhrase } from './cancelCopy';
import { ago, ini, m2t, plDni } from '../lib/ui';
import { SendMailDialog } from '../mail/SendMailDialog';
import { AiSummaryCard } from './AiSummaryCard';
import { CommsTab } from './CommsTab';
import { OrganizationTab } from './OrganizationTab';
import { PendingChangesCard } from './PendingChangesCard';
import { HistoryTab } from './HistoryTab';
import {
  answeredRefusals,
  standingWords,
  hoursLabel,
  issuesFor,
  lifeSteps,
  schedStateFor,
  unansweredShifts,
  STEP_TONES,
  type VolunteerRollup,
  type VolunteerShift,
} from './derive';
import { TagChip, TagPicker, useApplyVolunteerTags } from './TagBits';
import { ANSWER_CHIP, CARD, INPUT, MONO, SECTION_LABEL, seg, SEG_BTN, SECTION_LABEL as LBL } from './ui';
import { useUi } from '../state/ui';
import { ReadOnlyFieldset } from '../components/ReadOnlyFieldset';
import { useCoordinatorLabel } from '../lib/people';

export type PersonTab = 'profil' | 'komunikacija' | 'zgodovina' | 'organizacija';

const AVAIL_TONES: Record<AvailabilityValue, React.CSSProperties> = {
  'na-voljo': { background: 'oklch(0.955 0.035 155)', color: 'oklch(0.33 0.06 155)', border: '1px solid oklch(0.85 0.055 155)' },
  delno: { background: 'oklch(0.975 0.03 75)', color: 'oklch(0.38 0.07 75)', border: '1px solid oklch(0.87 0.06 75)' },
  'ni-na-voljo': { background: 'oklch(0.965 0.03 25)', color: 'oklch(0.44 0.1 25)', border: '1px solid oklch(0.88 0.05 25)' },
  neznano: { background: 'oklch(1 0 0)', color: 'oklch(0.55 0.012 90)', border: '1px dashed oklch(0.88 0.008 95)' },
};

const AVAIL_CYCLE: AvailabilityValue[] = ['na-voljo', 'delno', 'ni-na-voljo', 'neznano'];

const DAY_TONES = [
  { tint: 'oklch(0.955 0.022 205)', ink: 'oklch(0.45 0.062 205)' },
  { tint: 'oklch(0.958 0.02 95)', ink: 'oklch(0.44 0.048 95)' },
  { tint: 'oklch(0.958 0.022 315)', ink: 'oklch(0.45 0.07 315)' },
];

interface Form {
  name: string;
  phone: string;
  email: string;
  lang: string;
  gender: string;
  residence: string;
  age: string;
  experience: string;
  availability: Record<string, AvailabilityValue>;
  canMultiDay: boolean | null;
  otherTasks: boolean | null;
  wishes: string;
  note: string;
}

const formOf = (v: VolunteerRow): Form => ({
  name: v.name,
  phone: v.phone,
  email: v.email ?? '',
  lang: v.lang,
  gender: v.gender,
  residence: v.residence,
  age: v.age === null ? '' : String(v.age),
  experience: v.experience,
  availability: v.availability,
  canMultiDay: v.canMultiDay,
  otherTasks: v.otherTasks,
  wishes: v.wishes,
  note: v.note,
});

/** What the age field is stored as; anything else is no age at all. */
const ageOf = (text: string) => (/^\d{1,3}$/.test(text.trim()) ? Number(text.trim()) : null);

/** A slot left out and a slot marked `neznano` say the same thing. */
const knownAvailability = (a: Record<string, AvailabilityValue>) =>
  canonical(Object.fromEntries(Object.entries(a).filter(([, value]) => value !== 'neznano')));

/** When a draft field means what `volunteerInputSchema` would store. */
const FORM_SAME: FieldSame<Form> = {
  name: sameTrimmed,
  phone: sameTrimmed,
  email: (a, b) => a.trim().toLowerCase() === b.trim().toLowerCase(),
  gender: sameTrimmed,
  residence: sameTrimmed,
  age: (a, b) => ageOf(a) === ageOf(b),
  experience: sameTrimmed,
  availability: (a, b) => knownAvailability(a) === knownAvailability(b),
  wishes: sameTrimmed,
  note: sameTrimmed,
};

export function PersonDetail({
  volunteer: v,
  rollup: r,
  tags,
  days,
  tab,
  setTab,
  onDeleted,
}: {
  volunteer: VolunteerRow;
  rollup: VolunteerRollup;
  tags: TagRow[];
  days: EventDay[];
  tab: PersonTab;
  setTab: (t: PersonTab) => void;
  onDeleted: () => void;
}) {
  const mut = useVolunteerMutations();
  const navigate = useNavigate();
  const { setAsideTab } = useUi();
  // The organisation reads the profile and writes nothing here; what was sent,
  // answered or published is not theirs to see (SPEC §1.1L).
  const { me, can } = useSession();
  const canEdit = can('people:write');
  const canComms = can('comms:read');
  const shownTab: PersonTab =
    (tab === 'komunikacija' && !canComms) || (tab === 'zgodovina' && !can('audit:read')) ? 'profil' : tab;
  // The summary on Profil cites messages that Komunikacija shows, so the one it
  // points at — highlighted for a moment — outlives the switch between the tabs.
  const thread = useThread(v.id);
  const labelOf = useCoordinatorLabel();
  const aiEnabled = useEvent().data?.ai.enabled ?? false;
  // A fresh object per click, so clicking the same citation again restarts the moment.
  const [highlight, setHighlight] = useState<{ messageId: string } | null>(null);
  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => setHighlight(null), 2500);
    return () => clearTimeout(t);
  }, [highlight]);
  // The roster row is the baseline: a save writes its answer there, and so does
  // anybody else's edit, so "neshranjene spremembe" is a question about the
  // server rather than about when this page opened.
  const { draft: form, setDraft: setForm, dirty } = useDraft(formOf(v), FORM_SAME);
  const [tagOpen, setTagOpen] = useState(false);
  const applyTags = useApplyVolunteerTags();
  const [lifeOpen, setLifeOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // "Ne pride" (SPEC §1.1K): the dialog, then what it freed — for the strip
  // that offers the replacement search and the undo.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelStrip, setCancelStrip] = useState<CancelFreedShift[] | null>(null);
  const { goGap, goShift } = useDeepLinks();
  const answerMut = useShiftAnswerMutation();
  // Once marked, the card and "Vendarle pride…" read what the cancel freed and
  // where each of those shifts stands now.
  const out = isCancelled(v);
  const cancelPlan = useCancelPlan(v.id, out);
  const cancelRecord = out ? (cancelPlan.data?.cancelled ?? null) : null;
  // The others who read this person's inbox (skupni predal). A napotnica to one
  // of them is offered to the rest, so the stepper's send goes through the
  // review dialog instead of leaving straight away.
  const roster = useVolunteers();
  const siblings = mailboxSiblings(v, roster.data ?? []);
  const [stepperSend, setStepperSend] = useState(false);

  const saving = mut.update.isPending;

  const save = () => {
    if (!form.name.trim() || saving) return;
    mut.update.mutate(
      {
        id: v.id,
        updatedAt: v.updatedAt,
        name: form.name.trim(),
        phone: form.phone,
        email: form.email.trim() || null,
        lang: form.lang,
        gender: form.gender,
        residence: form.residence,
        age: ageOf(form.age),
        experience: form.experience,
        availability: form.availability,
        canMultiDay: form.canMultiDay,
        otherTasks: form.otherTasks,
        wishes: form.wishes,
        note: form.note,
      },
      { onSuccess: () => notifications.show({ message: 'Shranjeno.' }) },
    );
  };

  const sched = schedStateFor(v, r, labelOf);
  const issues = issuesFor(v, r, sched);
  const sendBriefing = () => {
    if (siblings.length > 0) {
      setStepperSend(true);
      return;
    }
    mut.sendBriefing.mutate(v.id, {
      onSuccess: (res) =>
        notifications.show({
          message: res.skipped ? `Preskočeno: ${res.skipped}` : 'Navodila poslana.',
          color: res.skipped ? 'yellow' : 'green',
        }),
    });
  };
  const publish = () =>
    mut.publish.mutate(v.id, {
      onSuccess: () =>
        notifications.show({
          message: 'Razpored objavljen — oseba ga vidi na svoji povezavi.',
          color: 'green',
        }),
    });
  // A whole-schedule confirmation writes "potrjena" onto every shift, so while
  // any shift is turned down it would overrule that answer. Then only the
  // shifts nobody has answered are confirmed, one by one (SPEC §1.1F).
  const refused = answeredRefusals(v, r);
  const rest = unansweredShifts(v, r);
  // The rows read the same words the button does, so the card cannot contradict itself.
  const words = standingWords(v);
  const confirmRest = async () => {
    let done = 0;
    for (const s of rest) {
      try {
        await answerMut.mutateAsync({ volunteerId: v.id, shiftId: s.id, status: 'ok' });
        done += 1;
      } catch {
        break;
      }
    }
    if (done > 0) {
      notifications.show({
        message: done === rest.length ? `Potrjenih izmen: ${done}.` : `Potrjenih izmen: ${done} od ${rest.length}.`,
        color: done === rest.length ? 'green' : 'yellow',
      });
    }
  };
  const confirmSchedule = () => {
    if (refused.length > 0) {
      void confirmRest();
      return;
    }
    // Who recorded it is the row's own actor ("uveljavil X"); the comment is left for words a person types.
    mut.setConfirmation.mutate({ volunteerId: v.id, status: 'potrjen' });
  };
  const confirmLabel = refused.length > 0 ? `Potrdi preostale (${rest.length})` : 'Označi kot potrjeno';
  const canConfirm = refused.length > 0 ? rest.length > 0 && !answerMut.isPending : true;

  const steps = lifeSteps(v, r, sched, {
    findGap: () => {
      setAsideTab('prostovoljci');
      void navigate('/casovnica');
    },
    openComms: () => setTab('komunikacija'),
    sendBriefing,
    publish,
    confirm: confirmSchedule,
    openShift: (s) => goShift(s.eventDayId, s.id),
  });
  const curIdx = steps.findIndex((s) => s.tone !== 'done');
  const doneCount = steps.filter((s) => s.tone === 'done').length;
  const allDone = curIdx < 0;
  const shownSteps = lifeOpen ? steps : allDone ? [] : steps.slice(curIdx);
  const hidden = lifeOpen ? 0 : allDone ? steps.length : curIdx;

  const summary = [
    form.experience || 'izkušenost ni znana',
    form.residence || null,
    form.age ? `${form.age} let` : null,
    `brifing v ${form.lang === 'en' ? 'ENG' : 'SLO'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  // Nobody chases somebody who is not coming; what they wrote still counts.
  const commsBadge = useMemo(() => (r.awaitingUs || r.noReply ? '!' : null), [r]);
  const visTags = tags.filter((t) => v.tagIds.includes(t.id));

  const rowLabel: React.CSSProperties = { fontSize: rem(13), color: 'oklch(0.42 0.012 90)' };

  return (
    <div style={{ padding: '18px 20px 40px' }}>
      {/* sticky header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 6,
          margin: '-18px -20px 14px',
          padding: '10px 20px 11px',
          background: 'oklch(0.978 0.004 95)',
          borderBottom: '1px solid oklch(0.9 0.008 95)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: out ? 'oklch(0.94 0.045 25)' : 'oklch(0.93 0.02 245)',
              color: out ? 'oklch(0.45 0.12 25)' : 'oklch(0.4 0.06 245)',
              fontSize: rem(13.5),
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {ini(form.name || '?')}
          </span>
          <input
            value={form.name}
            readOnly={!canEdit}
            onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
            style={{
              ...INPUT,
              minWidth: 0,
              flex: 1,
              maxWidth: 320,
              padding: '5px 8px',
              borderRadius: 7,
              fontSize: rem(18),
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: 'oklch(0.24 0.012 90)',
              borderColor: !canEdit
                ? 'transparent'
                : form.name.trim()
                  ? 'oklch(0.92 0.006 95)'
                  : 'oklch(0.7 0.12 25)',
            }}
          />
          <div style={{ flex: 1, minWidth: 4 }} />
          {canEdit ? (
            <>
              {confirmDelete ? (
                <>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    style={{ ...SEG_BTN, ...seg(false), flexShrink: 0, padding: '7px 11px', borderRadius: 7 }}
                  >
                    Prekliči
                  </button>
                  <button
                    onClick={() => {
                      // The server refuses while the person still holds a shift that
                      // has not started, and says so; the page leaves only on success.
                      mut.deactivate.mutate(v.id, {
                        onSuccess: onDeleted,
                        onSettled: () => setConfirmDelete(false),
                      });
                    }}
                    style={{
                      flexShrink: 0,
                      cursor: 'pointer',
                      padding: '7px 11px',
                      borderRadius: 7,
                      border: 0,
                      background: 'oklch(0.55 0.14 25)',
                      fontSize: rem(13),
                      fontWeight: 600,
                      fontFamily: 'inherit',
                      color: 'oklch(1 0 0)',
                    }}
                  >
                    Res izbriši
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  style={{
                    flexShrink: 0,
                    cursor: 'pointer',
                    padding: '7px 11px',
                    borderRadius: 7,
                    border: '1px solid oklch(0.9 0.03 25)',
                    background: 'oklch(1 0 0)',
                    fontSize: rem(13),
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    color: 'oklch(0.5 0.12 25)',
                  }}
                >
                  Izbriši
                </button>
              )}
              <button
                onClick={save}
                disabled={!dirty || saving}
                style={{
                  flexShrink: 0,
                  cursor: dirty ? 'pointer' : 'default',
                  padding: '7px 15px',
                  borderRadius: 7,
                  border: `1px solid ${dirty ? 'oklch(0.55 0.09 155)' : 'oklch(0.9 0.006 95)'}`,
                  background: dirty ? 'oklch(0.62 0.09 155)' : 'oklch(0.95 0.005 95)',
                  fontSize: rem(13),
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  color: dirty ? 'oklch(1 0 0)' : 'oklch(0.6 0.012 90)',
                }}
              >
                Shrani
              </button>
            </>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, flexWrap: 'wrap' }}>
          <div
            title="Potrditev velja za celoten razpored osebe"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 10px',
              borderRadius: 20,
              background: sched.bg,
              border: `1px solid ${sched.bd}`,
              flexShrink: 0,
            }}
          >
            <span style={{ fontSize: rem(12.5), fontWeight: 600, color: sched.fg, whiteSpace: 'nowrap' }}>
              {sched.label}
            </span>
            <span style={{ fontSize: rem(11.5), color: sched.fg, opacity: 0.75, whiteSpace: 'nowrap' }}>
              {sched.meta}
            </span>
          </div>
          {sched.pending && r.shifts.length > 0 && canEdit && canConfirm ? (
            <button
              onClick={confirmSchedule}
              style={{
                flexShrink: 0,
                cursor: 'pointer',
                padding: '5px 11px',
                borderRadius: 7,
                border: '1px solid oklch(0.83 0.06 155)',
                background: 'oklch(0.95 0.04 155)',
                fontSize: rem(12.5),
                fontWeight: 500,
                fontFamily: 'inherit',
                color: 'oklch(0.33 0.06 155)',
                whiteSpace: 'nowrap',
              }}
            >
              {confirmLabel}
            </button>
          ) : null}
          {!canEdit ? null : !out ? (
            <button
              onClick={() => setCancelOpen(true)}
              style={{
                flexShrink: 0,
                cursor: 'pointer',
                padding: '5px 11px',
                borderRadius: 7,
                border: '1px solid oklch(0.9 0.006 95)',
                background: 'oklch(1 0 0)',
                fontSize: rem(12.5),
                fontWeight: 500,
                fontFamily: 'inherit',
                color: 'oklch(0.42 0.012 90)',
                whiteSpace: 'nowrap',
              }}
            >
              Ne pride…
            </button>
          ) : cancelRecord ? (
            <ComeBackButton volunteer={v} record={cancelRecord} />
          ) : null}
          <span
            style={{
              fontSize: rem(12.5),
              color: 'oklch(0.5 0.012 90)',
              minWidth: 0,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {summary}
          </span>
          <span
            style={{
              fontSize: rem(12),
              color: dirty ? 'oklch(0.55 0.1 75)' : 'oklch(0.62 0.012 90)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {!canEdit ? '' : saving ? 'shranjujem …' : dirty ? 'neshranjene spremembe' : 'brez sprememb'}
          </span>
        </div>
      </div>

      {/* Only while they are still out: a return from the card settles it. */}
      {cancelStrip && out ? (
        <div
          role="status"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            margin: '-4px 0 14px',
            padding: '8px 12px',
            borderRadius: 9,
            background: 'oklch(0.985 0.02 320)',
            border: '1px solid oklch(0.89 0.05 320)',
            color: 'oklch(0.4 0.08 320)',
            fontSize: rem(13),
          }}
        >
          <span style={{ fontWeight: 600 }}>{v.name} — ne pride</span>
          <span style={{ color: 'oklch(0.52 0.06 320)', flex: 1, minWidth: 0 }}>
            {freedPhrase(cancelStrip.length)}
          </span>
          {cancelStrip[0] ? (
            <button
              onClick={() => {
                const f = cancelStrip[0]!;
                goGap({ eventDayId: f.eventDayId, postId: f.postId, fromMin: f.fromMin, toMin: f.toMin, shiftId: f.shiftId });
              }}
              style={{ cursor: 'pointer', padding: '4px 10px', borderRadius: 6, border: '1px solid oklch(0.35 0.05 245)', background: 'oklch(0.35 0.05 245)', color: 'oklch(1 0 0)', fontFamily: 'inherit', fontSize: rem(12.5), fontWeight: 600 }}
            >
              Poišči zamenjavo
            </button>
          ) : null}
          <button
            onClick={() =>
              mut.uncancel.mutate(
                { id: v.id, restore: cancelStrip.map((f) => f.shiftId) },
                {
                  // Staffing a slot from this strip first makes the undo a
                  // partial one; it says so rather than closing quietly.
                  onSuccess: (res) => {
                    setCancelStrip(null);
                    notifications.show({
                      color: res.outcome === 'carried' ? 'green' : 'yellow',
                      message: comeBackDone(res),
                    });
                  },
                },
              )
            }
            style={{ cursor: 'pointer', padding: '4px 10px', borderRadius: 6, border: '1px solid oklch(0.88 0.04 320)', background: 'oklch(1 0 0)', color: 'inherit', fontFamily: 'inherit', fontSize: rem(12.5), fontWeight: 500 }}
          >
            Razveljavi
          </button>
          <button
            title="zapri"
            onClick={() => setCancelStrip(null)}
            style={{ cursor: 'pointer', border: 0, background: 'none', color: 'inherit', fontFamily: 'inherit' }}
          >
            ✕
          </button>
        </div>
      ) : null}
      {cancelOpen ? (
        <CancelDialog
          target={v}
          onClose={() => setCancelOpen(false)}
          onDone={(freed) => setCancelStrip(freed)}
        />
      ) : null}

      {/* tabs */}
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', borderBottom: '1px solid oklch(0.92 0.006 95)', marginBottom: 16 }}>
        {(
          [
            ['profil', 'Profil'],
            ['komunikacija', 'Komunikacija'],
            ['zgodovina', 'Zgodovina'],
            ['organizacija', v.noteCount > 0 ? `Organizacija · ${v.noteCount}` : 'Organizacija'],
          ] as const
        )
          .filter(
            ([key]) =>
              (key !== 'komunikacija' || canComms) && (key !== 'zgodovina' || can('audit:read')),
          )
          .map(([key, label]) => {
          const on = shownTab === key;
          const badge =
            key === 'komunikacija'
              ? commsBadge
              : key === 'organizacija' && me && noteNeedsViewer(me.role, v)
                ? '!'
                : null;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                border: 0,
                background: 'none',
                cursor: 'pointer',
                padding: '0 0 9px',
                fontSize: rem(13.5),
                fontWeight: 500,
                fontFamily: 'inherit',
                color: on ? 'oklch(0.25 0.012 90)' : 'oklch(0.55 0.012 90)',
                borderBottom: `2px solid ${on ? 'oklch(0.45 0.05 245)' : 'transparent'}`,
                marginBottom: -1,
              }}
            >
              <span>{label}</span>
              {badge ? (
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: rem(11),
                    fontWeight: 700,
                    color: 'oklch(1 0 0)',
                    background: 'oklch(0.58 0.13 25)',
                    borderRadius: 10,
                    padding: '1px 5px',
                  }}
                >
                  {badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {shownTab === 'profil' ? (
        <>
          {/* Pot do razporeditve — for somebody not coming, what became of their shifts */}
          {out ? (
            <CancelledCard volunteer={v} record={cancelRecord} />
          ) : !canComms ? null : (
          <div style={{ ...CARD, padding: '14px 16px 15px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <span style={{ ...SECTION_LABEL, whiteSpace: 'nowrap' }}>POT DO RAZPOREDITVE</span>
              <span style={{ fontFamily: MONO, fontSize: rem(12), fontWeight: 600, color: 'oklch(0.4 0.012 90)', whiteSpace: 'nowrap' }}>
                {doneCount}/{steps.length}
              </span>
              <span style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)', minWidth: 0, flex: 1 }}>
                {allDone ? 'vse urejeno — nič ne čaka nate' : `naslednji korak: ${steps[curIdx]!.title}`}
              </span>
              <button
                onClick={() => setLifeOpen((o) => !o)}
                style={{
                  flexShrink: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                  padding: '4px 10px',
                  borderRadius: 7,
                  border: '1px solid oklch(0.92 0.006 95)',
                  background: 'oklch(0.985 0.004 95)',
                  fontSize: rem(12),
                  color: 'oklch(0.45 0.012 90)',
                  whiteSpace: 'nowrap',
                }}
              >
                {lifeOpen ? 'Strni' : 'Prikaži vse korake'}
              </button>
            </div>
            {hidden > 0 ? (
              <button
                onClick={() => setLifeOpen(true)}
                style={{
                  cursor: 'pointer',
                  font: 'inherit',
                  textAlign: 'left',
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '6px 11px',
                  borderRadius: 9,
                  border: '1px dashed oklch(0.9 0.02 155)',
                  background: 'oklch(0.99 0.008 155)',
                  width: '100%',
                }}
              >
                <span
                  style={{
                    width: 19,
                    height: 19,
                    flexShrink: 0,
                    borderRadius: '50%',
                    background: 'oklch(0.62 0.09 155)',
                    color: 'oklch(1 0 0)',
                    fontSize: rem(11.5),
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✓
                </span>
                <span style={{ fontSize: rem(12.5), color: 'oklch(0.42 0.05 155)' }}>
                  {hidden}{' '}
                  {hidden === 1 ? 'korak opravljen' : hidden === 2 ? 'koraka opravljena' : hidden < 5 ? 'koraki opravljeni' : 'korakov opravljenih'}
                </span>
              </button>
            ) : null}
            {shownSteps.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {shownSteps.map((s) => {
                  const t = STEP_TONES[s.tone];
                  return (
                    <div
                      key={s.title}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 11px',
                        borderRadius: 9,
                        background: t.bg,
                        border: `1px solid ${t.bd}`,
                      }}
                    >
                      <span
                        style={{
                          width: 19,
                          height: 19,
                          flexShrink: 0,
                          borderRadius: '50%',
                          background: t.dot,
                          color: 'oklch(1 0 0)',
                          fontSize: rem(11.5),
                          fontWeight: 700,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {t.mark}
                      </span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: rem(13.5), fontWeight: 600, color: t.fg }}>{s.title}</div>
                        <div style={{ fontSize: rem(12), color: 'oklch(0.55 0.012 90)', lineHeight: 1.35 }}>{s.meta}</div>
                      </div>
                      {s.alt ? (
                        <button onClick={s.alt.run} style={STEP_BTN}>
                          {s.alt.label}
                        </button>
                      ) : null}
                      {s.act ? (
                        <button onClick={s.run} style={STEP_BTN}>
                          {s.act}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' }}>
            {/* left column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              {/* OZNAKE */}
              <div style={{ ...CARD, padding: 16, position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                  <span style={SECTION_LABEL}>OZNAKE</span>
                  <span style={{ fontSize: rem(12), color: 'oklch(0.62 0.012 90)' }}>
                    — skupne vidijo vsi, osebne ◍ samo ti
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                  {visTags.map((t) => (
                    <TagChip
                      key={t.id}
                      tag={t}
                      onRemove={
                        canEdit
                          ? () => mut.setTags.mutate({ id: v.id, tagIds: v.tagIds.filter((id) => id !== t.id) })
                          : undefined
                      }
                    />
                  ))}
                  {visTags.length === 0 ? (
                    <span style={{ fontSize: rem(12.5), color: 'oklch(0.62 0.012 90)' }}>Brez oznak.</span>
                  ) : null}
                  {canEdit ? (
                    <button
                      onClick={() => setTagOpen((o) => !o)}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 11px',
                        borderRadius: 20,
                        fontSize: rem(12.5),
                        fontFamily: 'inherit',
                        background: 'oklch(0.985 0.004 95)',
                        color: 'oklch(0.42 0.012 90)',
                        border: '1px dashed oklch(0.85 0.008 95)',
                      }}
                    >
                      + Oznaka
                    </button>
                  ) : null}
                </div>
                {tagOpen ? (
                  <TagPicker
                    kind="volunteer"
                    tags={tags}
                    targets={[v]}
                    onApply={(delta) => applyTags([v], delta)}
                    onClose={() => setTagOpen(false)}
                    style={{ position: 'absolute', left: 16, right: 16, top: '100%', marginTop: -6 }}
                  />
                ) : null}
              </div>

              {/* Without people:write every field below is disabled — nothing to save (SPEC §1.1L). */}
              <ReadOnlyFieldset readOnly={!canEdit}>
                {/* KONTAKT IN PROFIL */}
                <div style={{ ...CARD, padding: 16 }}>
                  <div style={{ ...LBL, marginBottom: 12 }}>KONTAKT IN PROFIL</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '132px minmax(0, 1fr)', gap: '10px 12px', alignItems: 'center' }}>
                    <label style={rowLabel}>Telefon</label>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
                      placeholder="041 123 456"
                      style={{ ...INPUT, width: 180, fontFamily: MONO, fontSize: rem(13) }}
                    />
                    <label style={rowLabel}>E-pošta</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <input
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
                        placeholder="ime@primer.si"
                        style={{ ...INPUT, width: '100%', boxSizing: 'border-box', minWidth: 0 }}
                      />
                      {siblings.length > 0 && canComms ? (
                        <span
                          title="Isti naslov je vpisan še pri drugih: dobijo eno skupno sporočilo, vsak s svojo povezavo, in vsa pošta s tega naslova je v vseh njihovih nitih."
                          style={{
                            flexShrink: 0,
                            fontSize: rem(11.5),
                            padding: '2px 8px',
                            borderRadius: 20,
                            background: 'oklch(0.955 0.03 75)',
                            color: 'oklch(0.42 0.08 75)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          skupni predal z {joinNames(siblings.map((s) => s.name), 'sl')}
                        </span>
                      ) : null}
                    </div>
                    <label style={rowLabel}>Spol</label>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {GENDERS.map((g) => (
                        <button
                          key={g}
                          onClick={() => setForm({ ...form, gender: form.gender === g ? '' : g })}
                          style={{ ...SEG_BTN, ...seg(form.gender === g) }}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                    <label style={rowLabel}>Kraj bivanja</label>
                    <input
                      value={form.residence}
                      onChange={(e) => setForm({ ...form, residence: e.currentTarget.value })}
                      placeholder="npr. Bled"
                      style={{ ...INPUT, width: 220 }}
                    />
                    <label style={rowLabel}>Starost</label>
                    <input
                      value={form.age}
                      onChange={(e) => setForm({ ...form, age: e.currentTarget.value.replace(/\D/g, '') })}
                      placeholder="npr. 34"
                      style={{ ...INPUT, width: 80, fontFamily: MONO, fontSize: rem(13) }}
                    />
                    <label style={rowLabel}>Izkušenost</label>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {EXPERIENCE_LEVELS.map((x) => (
                        <button
                          key={x}
                          onClick={() => setForm({ ...form, experience: form.experience === x ? '' : x })}
                          style={{ ...SEG_BTN, ...seg(form.experience === x) }}
                        >
                          {x}
                        </button>
                      ))}
                    </div>
                    <label style={rowLabel}>Jezik brifinga</label>
                    <div style={{ display: 'flex', gap: 5 }}>
                      {(
                        [
                          ['sl', 'SLO'],
                          ['en', 'ENG'],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={value}
                          onClick={() => setForm({ ...form, lang: value })}
                          style={{ ...SEG_BTN, ...seg(form.lang === value) }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <label style={rowLabel}>Več dni</label>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      {(
                        [
                          [true, 'da'],
                          [false, 'ne'],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={label}
                          onClick={() =>
                            setForm({ ...form, canMultiDay: form.canMultiDay === value ? null : value })
                          }
                          style={{ ...SEG_BTN, ...seg(form.canMultiDay === value) }}
                        >
                          {label}
                        </button>
                      ))}
                      <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>lahko pomaga več različnih dni</span>
                    </div>
                    <label style={rowLabel}>Druge naloge</label>
                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                      {(
                        [
                          [true, 'da'],
                          [false, 'ne'],
                        ] as const
                      ).map(([value, label]) => (
                        <button
                          key={label}
                          onClick={() =>
                            setForm({ ...form, otherTasks: form.otherTasks === value ? null : value })
                          }
                          style={{ ...SEG_BTN, ...seg(form.otherTasks === value) }}
                        >
                          {label}
                        </button>
                      ))}
                      <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>predvidene tudi druge naloge</span>
                    </div>
                  </div>
                </div>

                {/* RAZPOLOŽLJIVOST */}
                <div style={{ ...CARD, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                    <span style={LBL}>RAZPOLOŽLJIVOST</span>
                    {canEdit ? (
                      <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>klik preklaplja stanje termina</span>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {(['d0', 'd1', 'd2'] as const).map((dayKey, di) => {
                      const day = days.find((d) => d.key === dayKey);
                      const slots = AVAILABILITY_SLOTS.filter((s) => s.dayKey === dayKey);
                      return (
                        <div key={dayKey} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                          <span
                            style={{
                              fontSize: rem(12),
                              fontWeight: 600,
                              width: 132,
                              flexShrink: 0,
                              padding: '2px 8px',
                              borderRadius: 5,
                              background: DAY_TONES[di]!.tint,
                              color: DAY_TONES[di]!.ink,
                              boxSizing: 'border-box',
                              marginTop: 4,
                            }}
                          >
                            {day ? day.label : dayKey.toUpperCase()}
                          </span>
                          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {slots.map((slot) => {
                              const val = form.availability[slot.id] ?? 'neznano';
                              return (
                                <button
                                  key={slot.id}
                                  title={`${slot.name} (${slot.hours}) — ${AVAILABILITY_VALUE_LABEL[val]}`}
                                  onClick={() => {
                                    const next = AVAIL_CYCLE[(AVAIL_CYCLE.indexOf(val) + 1) % AVAIL_CYCLE.length]!;
                                    const availability = { ...form.availability };
                                    if (next === 'neznano') delete availability[slot.id];
                                    else availability[slot.id] = next;
                                    setForm({ ...form, availability });
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    padding: '5px 9px',
                                    borderRadius: 6,
                                    fontSize: rem(12),
                                    fontWeight: 500,
                                    fontFamily: 'inherit',
                                    ...AVAIL_TONES[val],
                                  }}
                                >
                                  {slot.name}{' '}
                                  <span style={{ fontFamily: MONO, fontSize: rem(11), opacity: 0.7 }}>{slot.hours}</span>
                                  {val === 'delno' ? ' ◐' : val === 'na-voljo' ? ' ✓' : val === 'ni-na-voljo' ? ' ✕' : ''}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 11, flexWrap: 'wrap' }}>
                    {AVAIL_CYCLE.map((val) => (
                      <span key={val} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: rem(11.5), color: 'oklch(0.55 0.012 90)' }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            ...AVAIL_TONES[val],
                            display: 'inline-block',
                          }}
                        />
                        {AVAILABILITY_VALUE_LABEL[val]}
                      </span>
                    ))}
                  </div>
                </div>

                {/* ŽELJE */}
                <div style={{ ...CARD, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                    <span style={LBL}>KOMENTARJI IN ŽELJE PROSTOVOLJCA</span>
                    <span style={{ fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>iz prijavnice</span>
                  </div>
                  <textarea
                    value={form.wishes}
                    onChange={(e) => setForm({ ...form, wishes: e.currentTarget.value })}
                    placeholder="Npr. raje zjutraj, pride skupaj s partnerjem…"
                    style={{
                      ...INPUT,
                      width: '100%',
                      boxSizing: 'border-box',
                      minHeight: 60,
                      resize: 'vertical',
                      padding: '10px 11px',
                      borderRadius: 8,
                      lineHeight: 1.5,
                      background: 'oklch(0.99 0.003 95)',
                      color: 'oklch(0.3 0.012 90)',
                    }}
                  />
                </div>

                {/* INTERNE OPOMBE */}
                <div style={{ ...CARD, padding: 16 }}>
                  <div style={{ ...LBL, marginBottom: 10 }}>INTERNE OPOMBE</div>
                  <textarea
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.currentTarget.value })}
                    placeholder="Npr. lahko dela samo do 18h, potrebuje prevoz iz Radovljice, prvič sodeluje…"
                    style={{
                      ...INPUT,
                      width: '100%',
                      boxSizing: 'border-box',
                      minHeight: 88,
                      resize: 'vertical',
                      padding: '10px 11px',
                      borderRadius: 8,
                      lineHeight: 1.5,
                      background: 'oklch(0.99 0.003 95)',
                      color: 'oklch(0.3 0.012 90)',
                    }}
                  />
                </div>
              </ReadOnlyFieldset>
            </div>

            {/* right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              {/* DODELJENE IZMENE */}
              <div style={{ ...CARD, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
                  <span style={LBL}>DODELJENE IZMENE</span>
                  <span style={{ fontFamily: MONO, fontSize: rem(11.5), color: 'oklch(0.62 0.012 90)' }}>
                    {r.shifts.length} · {hoursLabel(r.minutes)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {r.shifts.map((s) => (
                    <ShiftRow
                      key={s.id}
                      shift={s}
                      standing={words && !out ? (words[s.id] ?? null) : undefined}
                      comment={v.shiftAnswers?.[s.id]?.comment}
                    />
                  ))}
                </div>
                {r.shifts.length === 0 ? (
                  <div style={{ fontSize: rem(12.5), color: 'oklch(0.6 0.012 90)' }}>
                    {out ? 'Oseba nima več nobene izmene.' : 'Ta oseba še ni dodeljena nobeni izmeni.'}
                  </div>
                ) : null}
              </div>

              {/* POVZETEK KOMUNIKACIJE — correspondence, so not the organisation's (SPEC §1.1L) */}
              {canComms ? (
                <AiSummaryCard
                  volunteerId={v.id}
                  enabled={aiEnabled}
                  messageIds={thread.data ? thread.data.map((m) => m.id) : null}
                  waiting={
                    r.awaitingUs
                      ? `Čaka naš odgovor · ${ago(r.waitDays)}.`
                      : r.noReply
                        ? `Na naše sporočilo ni odgovora že ${plDni(r.waitDays)}.`
                        : null
                  }
                  onHighlight={(messageId) => {
                    setHighlight({ messageId });
                    setTab('komunikacija');
                  }}
                />
              ) : null}

              {/* NEOBJAVLJENE SPREMEMBE — only once something has been published
                  and changed since; "brez objave" is the lifecycle's job above. */}
              {r.publication === 'neobjavljeno' ? <PendingChangesCard volunteer={v} /> : null}

              {/* POVEZAVA DO BRIFINGA */}
              {can('publish') ? <BriefingLinkCard volunteer={v} /> : null}

              {stepperSend ? (
                <SendMailDialog
                  volunteerId={v.id}
                  volunteerName={v.name}
                  templateKey="napotnica"
                  onClose={() => setStepperSend(false)}
                />
              ) : null}

              {issues.length > 0 ? (
                <div
                  style={{
                    background: 'oklch(0.985 0.02 25)',
                    border: '1px solid oklch(0.9 0.04 25)',
                    borderRadius: 11,
                    padding: '13px 14px',
                  }}
                >
                  <div style={{ fontSize: rem(12), fontWeight: 600, color: 'oklch(0.45 0.12 25)' }}>Zadeve za ureditev</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 7 }}>
                    {issues.map((i) => (
                      <div key={i.text} style={{ fontSize: rem(12.5), color: 'oklch(0.4 0.05 25)', lineHeight: 1.35 }}>
                        — {i.text}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}

      {shownTab === 'komunikacija' ? (
        <CommsTab volunteer={v} rollup={r} highlightId={highlight?.messageId ?? null} />
      ) : null}
      {shownTab === 'zgodovina' ? <HistoryTab volunteerId={v.id} /> : null}
      {shownTab === 'organizacija' ? <OrganizationTab volunteer={v} /> : null}
    </div>
  );
}

const DAY_PILL = [
  { bg: 'oklch(0.955 0.022 205)', fg: 'oklch(0.45 0.062 205)' },
  { bg: 'oklch(0.958 0.02 95)', fg: 'oklch(0.44 0.048 95)' },
  { bg: 'oklch(0.958 0.022 315)', fg: 'oklch(0.45 0.07 315)' },
];

const STEP_BTN: React.CSSProperties = {
  flexShrink: 0,
  cursor: 'pointer',
  font: 'inherit',
  padding: '6px 11px',
  borderRadius: 7,
  border: '1px solid oklch(0.91 0.006 95)',
  background: 'oklch(1 0 0)',
  fontSize: rem(12.5),
  fontWeight: 500,
  color: 'oklch(0.38 0.012 90)',
  whiteSpace: 'nowrap',
};

/**
 * `standing` is where the shift stands — `null` while it is still asked,
 * `undefined` where no chip belongs; every login is told it. `comment` is the
 * answer's words, which only a login shown correspondence has (SPEC §1.1L).
 */
function ShiftRow({
  shift: s,
  standing,
  comment,
}: {
  shift: VolunteerShift;
  standing?: ShiftAnswerView['status'] | null;
  comment?: string;
}) {
  const { goShift } = useDeepLinks();
  const tone = DAY_PILL[s.dayIndex % DAY_PILL.length]!;
  return (
    <div
      onClick={() => goShift(s.eventDayId, s.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 7,
        cursor: 'pointer',
        background: 'oklch(1 0 0)',
        border: `1px solid ${s.clash ? 'oklch(0.88 0.05 25)' : 'oklch(0.94 0.006 95)'}`,
      }}
    >
      <span
        style={{
          fontSize: rem(11),
          fontWeight: 600,
          padding: '1px 6px',
          borderRadius: 4,
          background: tone.bg,
          color: tone.fg,
          flexShrink: 0,
        }}
      >
        {s.dayLabel}
      </span>
      <span style={{ fontFamily: MONO, fontSize: rem(11.5), fontWeight: 600 }}>
        {m2t(s.fromMin)}–{m2t(s.toMin)}
      </span>
      <span style={{ fontSize: rem(12.5), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {s.postCode} · {s.postMicro}
      </span>
      {s.clash ? (
        <span style={{ fontSize: rem(11), fontWeight: 700, color: 'oklch(0.52 0.13 25)', flexShrink: 0 }}>prekrivanje</span>
      ) : null}
      {/* Only an assigned shift is asked anything: a proposal was never on a briefing. */}
      {standing !== undefined && s.state === 'assigned' ? (
        <span
          title={comment || undefined}
          style={{
            fontSize: rem(11),
            fontWeight: 600,
            padding: '1px 6px',
            borderRadius: 4,
            flexShrink: 0,
            color: ANSWER_CHIP[standing ?? 'none'].fg,
            background: ANSWER_CHIP[standing ?? 'none'].bg,
          }}
        >
          {ANSWER_CHIP[standing ?? 'none'].text}
        </span>
      ) : null}
    </div>
  );
}

function BriefingLinkCard({ volunteer: v }: { volunteer: VolunteerRow }) {
  const mut = useVolunteerMutations();
  const [link, setLink] = useState<string | null>(null);
  const btn: React.CSSProperties = {
    cursor: 'pointer',
    padding: '7px 11px',
    borderRadius: 7,
    border: '1px solid oklch(0.89 0.008 95)',
    background: 'oklch(1 0 0)',
    fontSize: rem(12.5),
    fontWeight: 500,
    fontFamily: 'inherit',
    color: 'oklch(0.33 0.012 90)',
  };
  return (
    <div style={{ ...CARD, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={SECTION_LABEL}>POVEZAVA DO BRIFINGA</span>
        <span style={{ fontSize: rem(11.5), color: v.hasToken ? 'oklch(0.45 0.07 155)' : 'oklch(0.62 0.012 90)' }}>
          {v.hasToken ? 'aktivna' : 'še ni ustvarjena'}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          onClick={() => mut.issueToken.mutate(v.id, { onSuccess: (res) => setLink(res.link) })}
          style={btn}
        >
          {v.hasToken ? 'Nova povezava' : 'Ustvari povezavo'}
        </button>
        {link ? (
          <button
            onClick={() => {
              void navigator.clipboard.writeText(link);
              notifications.show({ message: 'Povezava kopirana.' });
            }}
            style={btn}
          >
            Kopiraj
          </button>
        ) : null}
      </div>
      {link ? (
        <div
          style={{
            marginTop: 8,
            padding: '7px 9px',
            borderRadius: 7,
            background: 'oklch(0.978 0.004 95)',
            border: '1px solid oklch(0.92 0.006 95)',
            fontFamily: MONO,
            fontSize: rem(11.5),
            color: 'oklch(0.4 0.012 90)',
            wordBreak: 'break-all',
          }}
        >
          {link}
        </div>
      ) : null}
      <div style={{ marginTop: 8, fontSize: rem(11.5), color: 'oklch(0.64 0.012 90)', lineHeight: 1.4 }}>
        Pošiljanje navodil ustvari novo povezavo — stare prenehajo veljati.
      </div>
    </div>
  );
}
